import React, { CSSProperties } from 'react';
import type { Theme } from '@chakra-ui/react';
import {
  Box,
  Grid,
  GridItem,
  Text,
  HStack,
  VStack,
  Flex,
  useTheme,
} from '@chakra-ui/react';
import { dayjs } from 'src/libs/dayjs';
import { getIntensityColor, intensityColors } from 'src/theme';
import {
  CampaignCalendarProps,
  CampaignEvent,
  CalendarDay,
} from 'src/features/MM/types';

type CustomTheme = Theme & {
  colors: Theme['colors'] & {
    background: { paleGrey: string };
    text: { darkBlue: Record<number, string> };
  };
};

export const CampaignCalendar: React.FC<CampaignCalendarProps> = ({
  startDate,
  endDate,
  channels,
  events,
}) => {
  const theme = useTheme() as CustomTheme;

  const generateCalendarDays = (): CalendarDay[] => {
    const days: CalendarDay[] = [];
    const start = dayjs(startDate);
    const end = dayjs(endDate);

    let current = start;
    while (current.isBefore(end) || current.isSame(end)) {
      days.push({
        date: current.format('YYYY-MM-DD'),
        dayName: current.format('ddd'),
        isWeekend: current.day() === 0 || current.day() === 6,
      });
      current = current.add(1, 'day');
    }

    return days;
  };

  const calendarDays = generateCalendarDays();

  const getEventsForChannel = (
    channelId: string,
    channelStartDate: string,
    channelEndDate: string,
  ): CampaignEvent[] => {
    const start = dayjs(channelStartDate);
    const end = dayjs(channelEndDate);

    return events.filter(
      (event) =>
        event.channel === channelId &&
        (dayjs(event.startDate).isBefore(end) ||
          dayjs(event.startDate).isSame(end)) &&
        (dayjs(event.endDate).isAfter(start) ||
          dayjs(event.endDate).isSame(start)),
    );
  };

  const getEventBarStyle = (
    event: CampaignEvent,
    dayDate: string,
  ): CSSProperties => {
    const eventStart = dayjs(event.startDate);
    const eventEnd = dayjs(event.endDate);
    const dayStart = dayjs(dayDate);
    const dayEnd = dayStart.add(1, 'day');

    if (eventStart.isAfter(dayEnd) || eventEnd.isBefore(dayStart)) {
      return { display: 'none' } as const;
    }

    const calendarStart = dayjs(startDate);
    const dayIndex = dayStart.diff(calendarStart, 'day');
    const eventStartIndex = eventStart.diff(calendarStart, 'day');
    const eventEndIndex = eventEnd.diff(calendarStart, 'day');
    const eventSpanDays = eventEndIndex - eventStartIndex + 1;

    if (eventSpanDays === 1) {
      return { width: '100%', left: '0%', display: 'flex' } as const;
    }

    if (dayIndex === eventStartIndex) {
      const totalWidthPercent = eventSpanDays * 100;
      return {
        width: `${totalWidthPercent}%`,
        left: '0%',
        display: 'flex',
      } as const;
    }

    return { display: 'none' } as const;
  };

  return (
    <Box
      bg="surface.card"
      borderRadius="lg"
      p={6}
      boxShadow="sm"
      border="1px solid"
      borderColor="border.layout">
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontSize="2xl" fontWeight="bold" color="gray.800">
          Campaign Calendar
        </Text>
        <HStack spacing={1} align="center">
          {intensityColors.map((color, index) => (
            <Box key={index} w="3" h="3" bg={color} borderRadius="sm" />
          ))}
          <Text fontSize="sm" fontWeight="semibold" color="gray.600">
            Intensity
          </Text>
        </HStack>
      </Flex>

      <Box overflowX="auto">
        <Grid
          templateColumns={`100px repeat(${calendarDays.length}, 1fr)`}
          gap={0}
          minW={`${100 + calendarDays.length * 100}px`}>
          <GridItem />
          {calendarDays.map((day) => (
            <GridItem
              key={day.date}
              p={2}
              textAlign="center"
              borderBottom="1px solid"
              borderColor="border.layout"
              bg={day.isWeekend ? theme.colors.background.paleGrey : 'white'}>
              <VStack spacing={0}>
                <Text fontSize="sm" fontWeight="semibold" color="gray.600">
                  {dayjs(day.date).format('MMM, D')}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {day.dayName}
                </Text>
              </VStack>
            </GridItem>
          ))}

          {channels.map((channel) => (
            <React.Fragment key={channel.id}>
              <GridItem
                p={2}
                borderRight="1px solid"
                borderBottom="1px solid"
                borderColor="border.layout"
                display="flex"
                alignItems="center">
                <Text fontSize="sm" fontWeight="medium" color="gray.700">
                  {channel.name}
                </Text>
              </GridItem>

              {calendarDays.map((day) => {
                const dayEvents = getEventsForChannel(
                  channel.id,
                  day.date,
                  day.date,
                );
                return (
                  <GridItem
                    key={`${channel.id}-${day.date}`}
                    borderRight="1px solid"
                    borderBottom="1px solid"
                    borderColor="border.layout"
                    position="relative"
                    minH="50px"
                    bg={
                      day.isWeekend ? theme.colors.background.paleGrey : 'white'
                    }>
                    {dayEvents
                      .filter((event) => event.type === 'campaign')
                      .map((event, index) => (
                        <Box
                          key={`${event.id}-${index}`}
                          position="absolute"
                          top={`${index * 26}px`}
                          left="2px"
                          right="2px"
                          height="24px"
                          bg={getIntensityColor(event.intensity, event.type)}
                          borderRadius="24px"
                          display="flex"
                          alignItems="center"
                          px={2}
                          gap={1}
                          zIndex={2}
                          style={getEventBarStyle(event, day.date)}>
                          <Box
                            w="6px"
                            h="6px"
                            bg="surface.card"
                            borderRadius="50%"
                            flexShrink={0}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          />
                          <Text
                            fontSize="xs"
                            color={theme.colors.text.darkBlue[900]}
                            fontWeight="medium"
                            whiteSpace="nowrap"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            lineHeight="1"
                            display="flex"
                            alignItems="center"
                            height="100%">
                            Campaign
                          </Text>
                        </Box>
                      ))}

                    {dayEvents
                      .filter((event) => event.type === 'competitor')
                      .map((event, index) => (
                        <Box
                          key={`${event.id}-competitor-${index}`}
                          position="absolute"
                          bottom={`${index * 18}px`}
                          left="2px"
                          right="2px"
                          height="16px"
                          bg="#CECECE33"
                          border="1px dashed"
                          borderColor="#92A5B866"
                          borderRadius="24px"
                          display="flex"
                          alignItems="center"
                          px={2}
                          zIndex={2}
                          style={getEventBarStyle(event, day.date)}>
                          <Text
                            fontSize="xs"
                            color={theme.colors.text.darkBlue[900]}
                            fontWeight="medium"
                            whiteSpace="nowrap"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            lineHeight="1"
                            display="flex"
                            alignItems="center"
                            height="100%">
                            Competitor
                          </Text>
                        </Box>
                      ))}

                    {dayEvents
                      .filter((event) => event.type === 'holiday')
                      .map((event, index) => (
                        <Box
                          key={`${event.id}-holiday-${index}`}
                          position="absolute"
                          top={`${(dayEvents.filter((e) => e.type === 'campaign').length + index) * 26}px`}
                          left="2px"
                          right="2px"
                          height="24px"
                          bg={'black'}
                          borderRadius="24px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          style={getEventBarStyle(event, day.date)}>
                          <Text
                            fontSize="xs"
                            color="white"
                            fontWeight="medium"
                            whiteSpace="nowrap"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            lineHeight="24px"
                            textAlign="center"
                            width="100%"
                            height="100%"
                            display="flex"
                            alignItems="center"
                            justifyContent="center">
                            Holidays
                          </Text>
                        </Box>
                      ))}
                  </GridItem>
                );
              })}
            </React.Fragment>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};
